/** @jsx jsx */

import React from "react";

import { jsx, SerializedStyles } from "@emotion/core";
import { Input, Typography, Icon } from "antd";
import { FieldProps, Field, ErrorMessage } from "formik";

import { formHelperStyles, clearButtonStyles } from "./FormHelperStyles";

import { formHelperLabels as LABELS } from "./FormHelperLabels";

const { Text } = Typography;

interface Props {
  name: string;

  /**
   *  by default "name" is used to indicate the field name in UI
   *  but if a custom label is to be shown this overrides "name"
   */
  label?: string;

  mandatory?: boolean;

  disabled?: boolean;

  /**
   * props.placeholder is given highest priority
   * fallback: label or name
   */
  placeholder?: string;

  hideError?: boolean;

  customError?: string;

  /**
   * customStyles provided overrides default styles using
   * "Composition" behavior of emotion.
   * reference: https://emotion.sh/docs/composition
   */
  customStyles?: SerializedStyles;

  className?: string;

  /**
   * the keyboard focus goes to this element as soon as it is rendered.
   * Used it only for 1'st input.
   */
  autoFocus?: boolean;

  /**
   * Show suffix clear button icon for the input field.
   * onclick triggers onClear function.
   */
  showClear?: boolean;

  onClear?: () => void;

  onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * these props are not necessary for most cases!
   * in a case where a field needs a custom onChange,onBlur use this.
   * NOTE: providing this function will override the functions provided by formik.
   *       the custom functions wil have to update formik state on it's own
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

  /**
   * Required whenever some prefix has to be shown prior to the Input Field.
   * For example, for a read only Country code prior to the Mobile number field.
   */
  children?: React.ReactNode;
}

/**
 *
 * @param {Props} props
 * @returns a reusable, customizable input component to be used only within context of formik.
 */
const FormInput: React.FC<Props> = (props: Props) => {
  const {
    name,
    label,
    mandatory,
    disabled,
    onPressEnter,
    hideError,
    customError,
    onChange,
    onBlur,
    className,
    customStyles,
    autoFocus,
    children,
    showClear,
    onClear
  } = props;

  //props.placeholder is given highest priority
  // fallback: label or name
  const placeholder = props.placeholder || label || name;

  return (
    <div className={className} css={[formHelperStyles, customStyles]}>
      {label && (
        <label className="d-block" htmlFor={name}>
          {label}
          <Text className="mandatory">{mandatory && LABELS.mandatory}</Text>
        </label>
      )}
      <section className="d-flex">
        {children}
        <Field name={name}>
          {({ field }: FieldProps) => (
            <Input
              {...field}
              onChange={onChange || field.onChange}
              onBlur={onBlur || field.onBlur}
              placeholder={placeholder}
              autoFocus={autoFocus}
              disabled={disabled}
              suffix={
                showClear && (
                  <Icon
                    type="close-circle"
                    className="input-clear"
                    //Override input disable styles
                    style={clearButtonStyles}
                    onClick={onClear}
                  />
                )
              }
              onPressEnter={onPressEnter}
            />
          )}
        </Field>
      </section>
      {!hideError && (
        <ErrorMessage name={name}>
          {msg => {
            const errorMessage = customError || msg; //To make sure only 1 error message is shown.
            return <Typography className="error">{errorMessage}</Typography>;
          }}
        </ErrorMessage>
      )}
    </div>
  );
};

export default FormInput;
